/**
 * The order the two partner marks appear in, and which company each slot actually is.
 *
 * Three surfaces draw this lockup — the applicant form header, the staff dashboard chrome and the
 * email shell — and before this module each of them hardcoded both the order *and* the labels
 * inline. That is the arrangement in which "swap the logos" quietly becomes a bug: move the images
 * and the `alt` text stays behind, so a screen reader announces the NNPC mark as "Seplat Energy".
 *
 * So the two facts are separated. `logoLayout` on the brand profile still decides *which* marks
 * appear (both, or one alone); this decides what order they sit in, and the labels travel with the
 * slot rather than with the position.
 *
 * `primary` remains Seplat and `secondary` remains NNPC in the database — the uploaded files, the
 * `primary_only` / `secondary_only` layout options and every existing brand profile keep meaning
 * exactly what they meant. Only the display order changed.
 */

export type BrandSlot = "primary" | "secondary";

/**
 * NNPC leads, Seplat Energy follows — Brand, August 2026. Reversing this is a one-line
 * change here and nowhere else.
 */
export const LOCKUP_ORDER = ["secondary", "primary"] as const satisfies readonly BrandSlot[];

/**
 * Full names, for `alt` text and anywhere the mark is described rather than drawn.
 *
 * "NNPC", not "NNPC Limited" or "NNPC Ltd" — Brand, August 2026. The company is named the same way
 * everywhere it is written, and every surface reads these two constants rather than spelling it out,
 * so the rule holds in the email shell, the form header and the dashboard chrome at once.
 */
export const BRAND_SLOT_LABEL: Readonly<Record<BrandSlot, string>> = {
  primary: "Seplat Energy",
  secondary: "NNPC",
};

/** Short forms, for the text wordmarks that stand in until the image files are uploaded. */
export const BRAND_SLOT_SHORT_LABEL: Readonly<Record<BrandSlot, string>> = {
  primary: "Seplat",
  secondary: "NNPC",
};

/**
 * How the partnership is named in prose — the brand profile's seeded footer line, the dashboard
 * chrome and the pre-auth screens all read it from here.
 *
 * NNPC first, matching `LOCKUP_ORDER`, and no "Ltd"/"Limited", matching `BRAND_SLOT_LABEL`. It was
 * previously written out three times, in the order the marks used to appear, which is how the
 * footer came to contradict the logos directly above it.
 *
 * The live brand profile stores its own copy (staff can edit it in Brand settings), so changing
 * this constant changes the seeded default and the dashboard chrome; `syncBrandFooter.ts` is what
 * carries it to an existing profile.
 */
export const JOINT_VENTURE_LINE = "An NNPC | Seplat Joint Venture.";

/** The slots a layout asks for, already in lockup order. */
export const lockupSlots = (logoLayout: string | null | undefined): BrandSlot[] => {
  if (logoLayout === "primary_only") return ["primary"];
  if (logoLayout === "secondary_only") return ["secondary"];
  return [...LOCKUP_ORDER];
};
